import { fetchTenantSettings } from '@/services/tenant'
import _ from 'lodash'
import {
  buildTaxDisplayPolicy,
  resolveTenantTaxRegime,
  TAX_REGIMES
} from '@/utils/taxDisplay'

const DEFAULT_COUNTRY_REGION = 'Australia'
const DEFAULT_CURRENCY = 'AUD'
const DEFAULT_SUPPORT_EMAIL = 'support@flewidtech.com'
const COUNTRY_TO_CURRENCY = {
  Australia: 'AUD',
  India: 'INR',
  US: 'USD'
}
const CURRENCY_TO_LOCALE = {
  AUD: 'en-AU',
  INR: 'en-IN',
  USD: 'en-US'
}

function optionEnabledByDefault(value) {
  return value !== false && String(value).toLowerCase() !== 'false'
}

/**
 * Mutation types of the store
 */
const mutationTypes = {
  FETCHING: 'FETCHING',
  FETCHED: 'FETCHED'
}

/**
 * Initial state
 */
const state = {
  settings: undefined,
  loading: false,
  downForMaintenance: false,
  blockedByWaf: false
}

/**
 * Getters
 */
const getters = {
  auth0Realm(state) {
    return state.settings?.supplierPortalAuth0Realm
  },

  auth0ClientId(state) {
    return state.settings?.supplierPortalAuth0ClientId
  },

  featureSubscriptionEnabled(state) {
    return state.settings?.options?.subscriptionEnabled || false
  },

  featureBankAccountsEnabled(state) {
    return state.settings?.options?.bankAccountsEnabled || false
  },

  featureSupplierDirectRebatesEnabled(state) {
    return state.settings?.options?.supplierDirectRebatesEnabled || false
  },

  featureCreditReturnsEnabled(state) {
    return state.settings?.options?.creditReturnsEnabled || false
  },

  featureCouponsEnabled(state) {
    return state.settings?.options?.couponsEnabled !== false
  },

  orderChatEnabled(state) {
    return optionEnabledByDefault(state.settings?.options?.orderChatEnabled)
  },

  instructionsEnabled(state) {
    return state.settings?.options?.instructionsEnabled || false
  },

  userGuideEnabled(state) {
    return state.settings?.options?.userGuideEnabled || false
  },

  termsConditionsEnabled(state) {
    return state.settings?.options?.termsConditionsEnabled || false
  },

  privacyPolicyEnabled(state) {
    return state.settings?.options?.privacyPolicyEnabled || false
  },

  featureCreditReturnAutoEnrolmentEnabled(state) {
    return state.settings?.options?.creditReturnAutoEnrolment || false
  },

  featureDeliveryConfirmationEnabled(state) {
    return state.settings?.options?.deliveryConfirmationEnabled || false
  },

  featureForecastEnabled(state) {
    return state.settings?.options?.forecast || false
  },

  tenantAgreementEnabled(state) {
    return state.settings?.options?.tenantAgreementEnabled !== false
  },

  tenantPaymentTransactionsConfig(state) {
    const config = state.settings?.options?.paymentTransactionsConfig
    if (!config) return {}
    try {
      return JSON.parse(config)
    } catch {
      return {}
    }
  },

  ownerControlledSupplierPlatformFee(state) {
    return state.settings?.options?.ownerControlledSupplierPlatformFee
  },

  ownerControlledSupplierTradingTerms(state) {
    return state.settings?.options?.ownerControlledSupplierTradingTerms
  },

  ownerControlledProductTradingTerms(state) {
    return state.settings?.options?.ownerControlledProductTradingTerms
  },

  signupFlow(state) {
    return state.settings?.signupFlow
  },

  tenantTheme(state) {
    return state.settings?.theme
  },

  tenantThemeColours(state) {
    const theme = getters.tenantTheme(state)
    // The tenant theme can contain logos, json2pdf colours, etc. that we don't want to pass to the vuetify theme because
    // it won't be able to interpret them. So here we filter out any values that don't appear to be colours because
    // their values don't appear to start with either '#' or 'rgb'.
    return _.pickBy(theme || {}, (value, key) => {
      return typeof value === 'string' ? /^(#|rgb)/.test(value) : false
    })
  },
  tenantLogoDark(state) {
    return state.settings?.theme?.tenantLogoDark
  },

  tenantLogoLight(state) {
    return state.settings?.theme?.tenantLogoLight
  },

  tenantName(state) {
    return state.settings?.name
  },

  tenantCountryRegion(state) {
    return state.settings?.options?.countryRegion || DEFAULT_COUNTRY_REGION
  },

  tenantCurrency(state, getters) {
    return (
      state.settings?.options?.currency ||
      COUNTRY_TO_CURRENCY[getters.tenantCountryRegion] ||
      DEFAULT_CURRENCY
    )
  },

  tenantLocale(state, getters) {
    return CURRENCY_TO_LOCALE[getters.tenantCurrency] || 'en-AU'
  },

  tenantTaxRegime(state, getters) {
    return resolveTenantTaxRegime({
      taxRegime: state.settings?.options?.taxRegime,
      countryRegion: getters.tenantCountryRegion
    })
  },

  taxDisplayPolicy(state, getters) {
    return buildTaxDisplayPolicy({
      taxRegime: getters.tenantTaxRegime,
      countryRegion: getters.tenantCountryRegion
    })
  },

  usesAustralianTaxLabels(state, getters) {
    return getters.tenantTaxRegime === TAX_REGIMES.AU_GST
  },

  isILR(state) {
    return state.settings?.name?.toLowerCase() === 'ilrmarketplace'
  },

  featureCreditCardPaymentEnabled(state) {
    return (
      state.settings?.options?.supportedPaymentTypes?.indexOf('CREDIT_CARD') >=
      0
    )
  },

  featurePaymentServicesPaymentEnabled(state) {
    return (
      state.settings?.options?.supportedPaymentTypes?.indexOf(
        'PAYMENT_SERVICES'
      ) >= 0
    )
  },

  featureSupplierDirectPaymentEnabled(state) {
    return (
      state.settings?.options?.supportedPaymentTypes?.indexOf(
        'SUPPLIER_DIRECT'
      ) >= 0
    )
  },

  footerTagLine(state) {
    return state.settings?.footerTagLine
  },

  supportEmail(state) {
    return (
      state.settings?.options?.supportEmailSupplier ||
      state.settings?.options?.supportEmail ||
      DEFAULT_SUPPORT_EMAIL
    )
  },

  tenantTradingNames(state) {
    const tradingName = state.settings?.options?.tradingName

    if (Array.isArray(tradingName)) return tradingName
    if (typeof tradingName !== 'string') return []

    const trimmedTradingName = tradingName.trim()
    if (!trimmedTradingName) return []

    try {
      const parsedTradingName = JSON.parse(trimmedTradingName)
      if (Array.isArray(parsedTradingName)) return parsedTradingName
      if (typeof parsedTradingName === 'string') return [parsedTradingName]
    } catch {
      // keep fallback below
    }

    return [trimmedTradingName]
  },

  buyGetOptionEnabled(state) {
    return state.settings?.options?.buyGetFreeEnabled || false
  },

  faviconUrls(state) {
    const theme = state.settings?.theme || {}
    return {
      '16x16': theme.favicon16 || null,
      '32x32': theme.favicon32 || null,
      apple: theme.appleTouchIcon || null
    }
  }
}

/**
 * Actions
 */
const actions = {
  fetchTenantSettings({ commit }) {
    commit(mutationTypes.FETCHING)

    return fetchTenantSettings()
      .then((settings) => {
        commit(mutationTypes.FETCHED, settings)
      })
      .catch((error) => {
        commit(mutationTypes.FETCHED, {
          downForMaintenance: error.response?.status === 566,
          blockedByWaf: error.response?.status === 567
        })
        console.log(error)
      })
  }
}

/**
 * Mutations
 */
const mutations = {
  [mutationTypes.FETCHED](state, settings) {
    state.loading = false
    state.settings = settings
    state.downForMaintenance = settings.downForMaintenance || false
    state.blockedByWaf = settings.blockedByWaf || false
  },

  [mutationTypes.FETCHING](state) {
    state.loading = true
    state.settings = undefined
    state.downForMaintenance = false
    state.blockedByWaf = false
  }
}

export default {
  namespaced: true,
  actions,
  getters,
  mutations,
  state
}
